import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLang } from "../context/LanguageContext";

const easeElegant = [0.76, 0, 0.24, 1];

export const MarketAnalysis = () => {
  const { t } = useLang();
  const segments = t("market.segments") || [];
  const compare = t("market.compare") || {};
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);

  return (
    <section
      id="market"
      ref={ref}
      className="relative bg-[#0A0A0A] py-28 md:py-40 border-t border-white/10 overflow-hidden"
      data-testid="market-section"
    >
      {/* Background architecture image with parallax */}
      <motion.div
        className="absolute right-0 top-0 h-full w-1/3 opacity-25 hidden md:block"
        style={{ y }}
        aria-hidden
      >
        <img
          src="https://images.unsplash.com/photo-1621478493131-8597c4d4f5b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwZGV0YWlscyUyMGRhcmt8ZW58MHx8fHwxNzg0MDQxMzUzfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0A0A0A]" />
      </motion.div>

      <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-24">
          <div className="md:col-span-4">
            <div className="flex items-center gap-4">
              <span className="block h-px w-12 bg-[#C5A059]" />
              <span className="overline">{t("market.overline")}</span>
            </div>
          </div>
          <div className="md:col-span-8">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 1.1, ease: easeElegant }}
              className="font-serif text-[#F4F4F0] text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight max-w-[22ch]"
            >
              {t("market.title")}
            </motion.h2>
            <p className="mt-8 text-[#A1A1AA] text-base md:text-lg leading-[1.65] font-light max-w-[60ch]">
              {t("market.intro")}
            </p>
          </div>
        </div>

        {/* Segments */}
        <div className="grid md:grid-cols-3 border-t border-white/10 border-l">
          {segments.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-5% 0px" }}
              transition={{ duration: 1.0, delay: i * 0.08, ease: easeElegant }}
              className="border-r border-b border-white/10 p-8 md:p-10 lg:p-12 flex flex-col min-h-[380px] hover:bg-white/[0.02] transition-colors duration-500"
              data-testid={`market-segment-${s.n}`}
            >
              <div className="flex items-center justify-between mb-8">
                <span className="font-mono-ish text-xs text-[#C5A059] tracking-[0.28em]">
                  {s.n}
                </span>
                <span className="font-serif italic text-[#C5A059] text-3xl md:text-4xl leading-none">
                  {s.metric}
                </span>
              </div>
              <h3 className="font-serif text-[#F4F4F0] text-2xl md:text-3xl font-light leading-tight tracking-tight">
                {s.title}
              </h3>
              <p className="mt-6 text-[#A1A1AA] text-sm md:text-base leading-[1.65] font-light">
                {s.body}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Compare block */}
        <div className="mt-20 md:mt-28 grid md:grid-cols-12 gap-8 md:gap-16">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-8">
              <span className="block h-px w-12 bg-[#C5A059]" />
              <span className="overline">{t("nav.market")} · vs</span>
            </div>
            <h3 className="font-serif text-[#F4F4F0] text-3xl md:text-4xl lg:text-5xl font-light leading-[1.05] tracking-tight">
              {compare.title}
            </h3>
            <p className="mt-6 text-[#A1A1AA] text-base leading-[1.65] font-light max-w-[46ch]">
              {compare.body}
            </p>
          </div>
          <div className="md:col-span-7 border-t border-white/10">
            {(compare.rows || []).map((row, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-5% 0px" }}
                transition={{ duration: 0.8, delay: i * 0.06, ease: easeElegant }}
                className="grid grid-cols-12 gap-4 py-6 border-b border-white/10 items-baseline"
              >
                <div className="col-span-4 md:col-span-3">
                  <span className="font-mono-ish text-xs text-[#52525B] tracking-[0.28em] uppercase">
                    0{i + 1}
                  </span>
                  <div className="mt-2 font-serif italic text-[#C5A059] text-xl">
                    {row.label}
                  </div>
                </div>
                <div className="col-span-8 md:col-span-9 font-serif text-[#F4F4F0] text-xl md:text-2xl font-light tracking-tight">
                  {row.value}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
